import * as THREE from "three";
import { customerLook, type CustomerPose } from "../game/customers";

// Customers, low-poly: legs, a shirt, arms, a head with hair, and the order
// number over their head while they wait. They walk with a swing, tap at the
// kiosk, carry their basket off, or shake their head if they gave up. Where
// they are comes from `customerPose`; this only draws it.

const SHIRTS = [0xe4473b, 0x3b8fd9, 0x45b06a, 0xf4b942, 0x8e5bd1, 0xf08a4b, 0x2fb5a8, 0xf2f2ee, 0x34495e, 0xd94f8a];
const SKINS = [0xf6d3b3, 0xe8b98f, 0xc68a5e, 0x9a6440, 0x6e4529];
const HAIRS = [0x2b1d14, 0x5a3a22, 0xc89b52, 0x111111, 0x8a3b1f, 0xb8b8b8];
const SHORTS = [0x2c3e50, 0x6d7a86, 0xd8c7a0, 0x3b5f8f];

/** Outdoor paint, carrying its own daylight like the boardwalk's. */
const daylit = (color: number) => new THREE.MeshLambertMaterial({ color, emissive: color, emissiveIntensity: 0.5 });
const pick = <T,>(list: readonly T[], f: number): T => list[Math.floor(f * list.length) % list.length]!;

function numberTag(no: number): THREE.Sprite {
  const canvas = document.createElement("canvas");
  canvas.width = 128;
  canvas.height = 64;
  const g = canvas.getContext("2d")!;
  g.fillStyle = "#ffffff";
  g.beginPath();
  g.roundRect(4, 4, 120, 56, 14);
  g.fill();
  g.lineWidth = 5;
  g.strokeStyle = "#2b2d42";
  g.stroke();
  g.fillStyle = "#2b2d42";
  g.font = "900 40px system-ui, sans-serif";
  g.textAlign = "center";
  g.textBaseline = "middle";
  g.fillText(`#${no}`, 64, 35);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex }));
  sprite.scale.set(0.34, 0.17, 1);
  return sprite;
}

export class PersonModel {
  readonly group = new THREE.Group();
  private readonly legs: THREE.Mesh[] = [];
  private readonly arms: THREE.Group[] = [];
  private readonly head = new THREE.Group();
  private readonly basket: THREE.Mesh;
  private readonly tag: THREE.Sprite;
  private readonly height: number;

  constructor(no: number) {
    const look = customerLook(no);
    this.height = 1.55 + look.height * 0.35;
    const k = this.height / 1.75;
    const shirt = daylit(pick(SHIRTS, look.shirt));
    const skin = daylit(pick(SKINS, look.skin));
    const shorts = daylit(pick(SHORTS, look.hair));
    const hair = daylit(pick(HAIRS, look.hair));

    const legGeo = new THREE.BoxGeometry(0.12, 0.8 * k, 0.13);
    legGeo.translate(0, -0.4 * k, 0);
    for (const dx of [-0.08, 0.08]) {
      const leg = new THREE.Mesh(legGeo, shorts);
      leg.position.set(dx, 0.82 * k, 0);
      this.legs.push(leg);
      this.group.add(leg);
    }
    const body = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.17, 0.62 * k, 10), shirt);
    body.position.y = 1.12 * k;
    this.group.add(body);
    const armGeo = new THREE.BoxGeometry(0.09, 0.58 * k, 0.1);
    armGeo.translate(0, -0.27 * k, 0);
    for (const dx of [-0.26, 0.26]) {
      const arm = new THREE.Group();
      arm.add(new THREE.Mesh(armGeo, shirt));
      const hand = new THREE.Mesh(new THREE.SphereGeometry(0.05, 6, 4), skin);
      hand.position.y = -0.58 * k;
      arm.add(hand);
      arm.position.set(dx, 1.4 * k, 0);
      this.arms.push(arm);
      this.group.add(arm);
    }
    const face = new THREE.Mesh(new THREE.SphereGeometry(0.13, 12, 8), skin);
    const cap = new THREE.Mesh(new THREE.SphereGeometry(0.135, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2), hair);
    // Hair sits back from the face.
    cap.position.set(0, 0.02, 0.025);
    const eyeMat = new THREE.MeshBasicMaterial({ color: 0x1b1b1b });
    for (const dx of [-0.045, 0.045]) {
      const eye = new THREE.Mesh(new THREE.SphereGeometry(0.018, 6, 4), eyeMat);
      eye.position.set(dx, -0.01, -0.12);
      this.head.add(eye);
    }
    this.head.add(face, cap);
    this.head.position.y = 1.6 * k;
    this.group.add(this.head);

    // Their basket, carried in front when they've got it.
    this.basket = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.1, 0.22), daylit(0xd8392b));
    this.basket.position.set(0, 1.0 * k, -0.25);
    this.basket.visible = false;
    this.group.add(this.basket);

    this.tag = numberTag(no);
    this.tag.position.y = this.height + 0.28;
    this.group.add(this.tag);
  }

  update(pose: CustomerPose, time: number, phase: number): void {
    this.group.position.set(pose.x, 0, pose.z);
    this.group.rotation.y = pose.facing;
    const walking = pose.action === "walk" || pose.action === "leave";
    const swing = walking ? Math.sin(time * 8 + phase) * 0.5 : 0;
    this.legs[0]!.rotation.x = swing;
    this.legs[1]!.rotation.x = -swing;
    const [left, right] = this.arms as [THREE.Group, THREE.Group];
    left.rotation.set(-swing * 0.7, 0, 0);
    right.rotation.set(swing * 0.7, 0, 0);
    if (pose.action === "tap") {
      // Reaching out to the screen, tapping.
      right.rotation.x = 1.25 + Math.max(0, Math.sin(time * 9 + phase)) * 0.2;
    } else if (pose.action === "collect" || pose.carrying) {
      left.rotation.x = right.rotation.x = 1.1;
    }
    this.basket.visible = pose.carrying;
    // A bob as they walk, a sway as they wait.
    this.group.position.y = walking ? Math.abs(Math.sin(time * 8 + phase)) * 0.03 : 0;
    this.head.rotation.y = pose.upset ? Math.sin(time * 10 + phase) * 0.45 : pose.action === "wait" ? Math.sin(time * 0.7 + phase) * 0.25 : 0;
    this.group.rotation.z = pose.action === "wait" ? Math.sin(time * 1.1 + phase) * 0.02 : 0;
    this.tag.visible = pose.action === "wait" || pose.action === "tap";
  }

  dispose(): void {
    this.group.removeFromParent();
    (this.tag.material as THREE.SpriteMaterial).map?.dispose();
  }
}
